/**
 * @fileoverview Base class for internal searchers (Bookmarks, History, SavedSearch).
 * Handles local data storage, filtering, and optional map layer display.
 * @module searchers/IntSearcher
 */

import { Searcher } from './Searcher.js';
import { escapeHtml, buildSearchResult, updateSearchPackWithSearchResult } from '../utils/jrfuncs.js';
import { PTYPES, STUB_SERIALIZATION_FIELDS, CUSTOM_ITEM_FIELDS_ALL } from '../constants.js';



/**
 * Base class for searchers that search/filter internal (local) data.
 *
 * Provides:
 * - Local data persistence via Lister/StateManager
 * - Filtering of internal results
 * - Optional map layer display
 * - Support for both POI and search-type results
 *
 * Subclasses: BookmarkSearcher, HistorySearcher, SavedSearcher
 *
 * @class
 * @extends Searcher
 */
export class IntSearcher extends Searcher {
    /**
     * @param {Object} mapApp - The MapApp instance
     * @param {Object} config - Configuration options
     * @param {boolean} [config.allowsResultTypePoi] - Whether POI results are allowed
     * @param {Array<string>} [config.filterFields] - Fields to search when filtering
     * @param {number} [config.maxItems] - Maximum items to store (null for unlimited)
     * @param {boolean} [config.addNewToTop] - Whether new items are added at top
     */
    constructor(mapApp, config = {}) {
        super(mapApp, {
            ...config
        });

        // Internal Searcher specific configuration
        this.allowsResultTypePoi = config.allowsResultTypePoi ?? false;
        this.allowsResultTypeSearch = config.allowsResultTypeSearch ?? false;

        // Filter options
        this.filterFields = config.filterFields || ['label'];
        this.timeStampField = config.timeStampField || null;
    }




    // ==========================================
    // ACCESSORS
    // ==========================================

    /**
     * Checks if this searcher allows POI-type results.
     * Internal Searchers can optionally allow POI results.
     * @returns {boolean} True if POI results are allowed
     * @override
     */
    doesAllowResultTypePoi() {
        return this.allowsResultTypePoi;
    }

    /**
     * Checks if this searcher allows search-type results (saved searches).
     * Internal Searchers always allow search results.
     * @returns {boolean} True
     * @override
     */
    doesAllowResultTypeSearch() {
        return this.allowsResultTypeSearch;
    }

    /**
     * Get the current filter query from the input.
     * @returns {string} The current query or empty string
     */
    getFilterQueryInput() {
        const filterInput = document.getElementById(this.inputId);
        return filterInput ? filterInput.value.trim() : '';
    }

    /**
     * Gets filtered results based on the current input query.
     * @returns {Array} Array of filtered results
     */
    getFilteredResults() {
        const filterQueryInput = this.getFilterQueryInput();
        const searchPack = {
            query: filterQueryInput,
            queryOriginal: filterQueryInput,
        };
        const searchResult = this.performFilteredSearchWorkFindInternalResults(searchPack);
        return searchResult;
    }

    /**
     * Gets filtered results for Internal Searchers
     * Returns filtered results based on current filter query.
     * @returns {Array} Array of result items
     * @override
     */
    getInternalFilteredResultsIfAppropriate() {
        const searchResult = this.getFilteredResults();
        return searchResult.results;
    }


    /**
     * Returns false since this is an internal searcher, not a database searcher.
     * @returns {boolean} False
     * @override
     */
    getIsDbSearcher() {
        return false;
    }






    // ==========================================
    // Core Filter Operations
    // ==========================================

    /**
     * Display internal results filtered by the current query.
     * Normalizes the query, filters internal results, sorts, and updates the lister and map.
     * @param {Object} searchPack - The search pack with query and options
     * @override
     */
    async performSearchWork(searchPack) {
        this.normalizeQueryOrGetQueryFromInputIfBlank(searchPack);
        const searchResult = this.performFilteredSearchWorkFindInternalResults(searchPack);

        // sort
        this.doSortSearchResult(searchResult);

        // NOW (NEW); update searchPack with result summary!
        updateSearchPackWithSearchResult(searchPack, searchResult);

        // Use lister renderer with total count info
        this.lister.showFilteredResultsFromSearchPackDontSaveLocally(searchResult, searchPack);

        if (this.mapLayerName) {
            // Auto update map layer with filtered results
            this.updateMapResultLayer(searchResult.results);
        }
    }


    /**
     * Normalizes the query in the search pack, or reads from the filter input if blank.
     * Trims whitespace and stores the normalized query back into the search pack.
     * @param {Object} searchPack - The search pack to normalize
     * @returns {string} The normalized query string
     */
    // ATTN: 1/23/26 im not sure about this
    normalizeQueryOrGetQueryFromInputIfBlank(searchPack) {
        const queryNormalized = (searchPack.query != null) ? searchPack.query.trim() : this.getFilterQueryInput().trim();
        // store it
        searchPack.query = queryNormalized;
        //
        return queryNormalized;
    }



    /**
     * Find entries matching the filter query.
     * If query is non-empty, filters by configured fields; otherwise returns every item.
     * Override in subclasses for custom filtering behavior.
     * @param {Object} searchPack - The search pack with query string
     * @returns {Object} Search result object with results array and totalCount
     * @protected
     */
    performFilteredSearchWorkFindInternalResults(searchPack) {
        let results;
        if (searchPack.query && searchPack.query.length > 0) {
            results = this.lister.filterResultsByFields(searchPack, this.filterFields);
        } else {
            results = this.lister.getAllResultsCopy();
        }

        // NOW (NEW); update searchPack with result summary!
        const totalCount = this.lister.getResultCount();
        const searchResult = buildSearchResult(results, totalCount);
    
        return searchResult;
    }


    /**
     * Performs operations after completing a filter/search.
     * Internal Searchers don't save to history by default.
     * @param {Object} searchPack - The search pack with query and options
     * @override
     */
    performPostSearchOperations(searchPack) {
        // Internal Searchers don't automatically save to history
        // Override in subclasses if needed

        // call super class
        super.performPostSearchOperations(searchPack);
    }








    // ==========================================
    // Generic CRUD Operations
    // ==========================================

    /**
     * Adds an item directly to the Internal Searcher as a reference (no copy).
     * Persists results to storage after adding.
     * @param {Object} item - The item to add
     * @param {string} debugMessage - Debug message for logging
     * @param {boolean} flagResetTimestamp - Whether to reset the timestamp on the item
     * @returns {Object|null} The added item or null if failed
     */
    addItemRefDirect(item, debugMessage, flagResetTimestamp) {
        // Add a REFERNCE to this existing item (dont make a copy)
        const flagBatchModeDontUpdateState = false;
        const retv = this.addResultRef(item, debugMessage, flagResetTimestamp);

        if (!flagBatchModeDontUpdateState) {
            // save and persist this result list
            this.saveResultsToStorage();
        }
        return retv;
    }

    /**
     * Removes an item by its unique identifier.
     * @param {string} uid - The unique identifier of the item to remove
     * @param {Object} item - The item being removed (may be used by subclasses for cleanup)
     * @param {boolean} flagUpdateStorageAndRefresh - Whether to persist changes and refresh display
     * @returns {boolean} True if removed successfully
     */
    removeItemByUidItem(uid, item, flagUpdateStorageAndRefresh) {
        //console.log(`IN INT SEARCHER ${this.searcherName} removeItemByUid`);
        const retv = this.lister.removeResultById(uid, flagUpdateStorageAndRefresh);
        // save and persist this result list
        this.saveResultsToStorage();
        return true;
    }

    /**
     * Checks if an item exists by its unique identifier.
     * @param {string} uid - The unique identifier to check
     * @returns {boolean} True if the item exists
     */
    hasItemByUid(uid) {
        return this.lister.getResultById(uid) !== null;
    }

    /**
     * Toggles the presence of an item by its unique identifier.
     * If the item exists, removes it. If not, adds it.
     * @param {string} uid - The unique identifier
     * @param {Object} item - The item to add (used when adding)
     * @returns {boolean} True if the item is now present, false if removed
     */
    addOrRemoveItemByUidItem(uid, item, debugMessage) {
        if (this.hasItemByUid(uid)) {
            const flagUpdateStorageAndRefresh = true;
            this.removeItemByUidItem(uid, item, flagUpdateStorageAndRefresh);
            return false;
        }
        const flagResetTimestamp = false;
        const retv = this.addItemRefDirect(item, debugMessage, flagResetTimestamp) !== null;
        return retv;
    }




    // ====================


    /**
     * Adds a search result to internal results.
     * Creates a search-type item from the search pack and stores it.
     * Used by HistorySearcher and SavedSearcher.
     * @param {Object} searchPack - The search pack with queryOriginal, searchType, totalCount, selectedRegion, regionCheckbox
     * @param {string} debugComment - Debug comment for logging
     * @returns {Object|null} The added item or null if failed
     */
    addSavedSearchItemManually(searchPack, debugComment) {

        //console.log(`DEBUG: In addSavedSearchItemManually with searchPack="${JSON.stringify(searchPack)}".`);

        // destructure
        const {queryOriginal, searchType, totalCount, selectedRegion, regionCheckbox} = searchPack;

        const uid = `search_${searchType}_${Date.now()}`;

        const item = {
            uid: uid,
            ptype: PTYPES.SEARCH,
            // from searchPack
            searchQuery: queryOriginal,
            searchType: searchType,
            searchTotalCount: totalCount,
            ...(selectedRegion && { selectedRegion: selectedRegion }),
            ...(regionCheckbox && { regionCheckbox: regionCheckbox })
        }

        //console.log(`DEBUG: In addSavedSearchItemManually with item="${JSON.stringify(item)}" and searchPack = "${JSON.stringify(searchPack)}".`)

        const flagResetTimestamp = true;
        const flagBatchModeDontUpdateState = false;
        let retv = this.addResultCopy(item, debugComment, flagResetTimestamp);
        //
        // refresh list to show this item is now in the list
        // we re-run this internal search, which is just a fast thin filter over internal results
        // we could re-run this internal search, which is just a fast thin filter over internal results - but this also causes map feature redraw
        // so the question is should we force a refresh
        if (this == this.mapApp.savedSearcher) {
             this.mapApp.displayNeedsRefresh_AfterSavedSearchStatusChanged(uid);
        } else if (this == this.mapApp.historySearcher) {
             this.mapApp.displayNeedsRefresh_AfterResultItemAddHistory(this);
        } else {
            console.log('ATTN: ERROR: addSavedSearchItemManually unknown searcher.');
        }

        //
        if (!flagBatchModeDontUpdateState) {
            // save and persist this result list
            this.saveResultsToStorage();
        }
        return retv;
    }






    addPdfLeadItemManually(label, pageNumber) {
        //console.log(`DEBUG: In addPdfLeadItemManually.`);

        const uid = `pdfLead_${Date.now()}`;

        const pdfBaseFileName = this.mapApp.getBasePdfFilename();
        const item = {
            uid: uid,
            ptype: PTYPES.PDFLEAD,
            lead: label,
            dName: label,
            address: `${pdfBaseFileName} (p.${pageNumber})`,
        }

        const flagResetTimestamp = true;
        const flagBatchModeDontUpdateState = false;
        const debugComment = 'adding pdf lead manually';
        let retv = this.addResultCopy(item, debugComment, flagResetTimestamp);
        //
        // refresh list to show this item is now in the list
        // we re-run this internal search, which is just a fast thin filter over internal results
        // we could re-run this internal search, which is just a fast thin filter over internal results - but this also causes map feature redraw
        // so the question is should we force a refresh
        if (this == this.mapApp.historySearcher) {
             this.mapApp.displayNeedsRefresh_AfterResultItemAddHistory(this);
        }

        //
        if (!flagBatchModeDontUpdateState) {
            // save and persist this result list
            this.saveResultsToStorage();
        }
        return retv;
    }

















    // ==========================================
    // Delayed Save Stubs
    // ==========================================

    /**
     * Internal Searchers don't use delayed save by default.
     * @override
     */
    clearDelayedLiveSearchSaveTrigger() {
        // No-op for Internal Searchers
    }







    /**
     * Re-runs a search from an existing search pack.
     * IntSearcher overrides this to simply clear and re-run, since "show all"
     * for internal searchers means clearing the filter to display all items.
     * @param {Object} searchPack - The search pack (unused; filter is cleared instead)
     * @override
     */
    reRunSearchPack(searchPack) {
        // Re-Run a search (this is triggered from user clicking on "show-all" or "re-randomize" link); NOTE that IntSearcher overrides this
        // IntSearcher overrides this because the only think to re-run on show all is to CLEAR the search inputs and run a clear search
        this.performClearSearch(true);
    }







    // ==========================================
    // SERIALIZED DATA
    // ==========================================



    /**
     * Serializes the current results into stub objects for localStorage persistence.
     * Uses STUB_SERIALIZATION_FIELDS to determine which fields to include per searcher type.
     * @returns {Array} Array of serialized stub objects
     * @override
     */
    serializeData() {
        // build the serialized data for this searcher
        const serializationFields = ['uid', ...STUB_SERIALIZATION_FIELDS[this.searcherName]];
        const results = this.lister.getInternalResults();
        const mapApp = this.getMapApp();
        //
        const serializedData = [];
        for (const item of results) {
            const stub = mapApp.createItemCustomStubForSerializing(item, serializationFields);
            serializedData.push(stub);
        }

        return serializedData;
    }


    /**
     * Deserializes stub data back into full result items by locating them in POI data.
     * A POI that can no longer be found in the POI database is KEPT as its stub (ptype POISTUB), so the
     * user's entry and notes survive a data change; the list shows it as an unknown item.
     * @param {Array} serializedData - Array of serialized stub objects from localStorage
     * @returns {Array} Array of reconstituted result items
     * @override
     */
    unSerializeData(serializedData) {
        // build the unserialized results from serialized stub data
        const mapApp = this.getMapApp();
        const results = [];
        for (const stubItem of serializedData) {
            const item = mapApp.locateAndModifyPoiFromLoadedUserStub(stubItem, CUSTOM_ITEM_FIELDS_ALL, false);
            if (item) {
                if (item === stubItem && typeof item === 'object' && item.ptype !== PTYPES.POISTUB) {
                    // A non-POI item (a search, a pdf lead, a custom map pin) is stored whole and handed
                    // back as it was, so its isCustomized flag -- which the custom pin style and the
                    // lister need -- is only as good as the stored copy. Recompute it from the item's
                    // own fields, as a found POI gets it recomputed by the merge above.
                    mapApp.updateItemIsCustomized(item);
                }
                results.push(item);
            }
        }
        return results;
    }



}
